export class FrameGrabber {
  private canvasContext: CanvasRenderingContext2D;
  private videoElement?: HTMLVideoElement;
  private placeholderImage?: HTMLImageElement;
  private isVideoEnabled = false;
  private isVideoPlaying = false;
  private size: { width: number; height: number };

  constructor(size?: { width: number; height: number }) {
    const canvas = document.createElement('canvas');
    this.canvasContext = canvas.getContext('2d')!;
    this.size = size ?? { width: 0, height: 0 };
    canvas.width = this.size.width;
    canvas.height = this.size.height;

    this.setVideoPlaying = this.setVideoPlaying.bind(this);
    this.setVideoPaused = this.setVideoPaused.bind(this);
  }

  init(videoElement: HTMLVideoElement, placeholderImage?: HTMLImageElement): void {
    this.setVideoElement(videoElement);
    if (placeholderImage) {
      this.setPlaceholderImage(placeholderImage);
    }
  }

  setVideoElement(videoElement: HTMLVideoElement): void {
    this.cleanupVideoListening();
    this.videoElement = videoElement;
    if (!this.videoElement.paused) {
      this.setVideoPlaying();
    }
    this.setupVideoListening();
  }

  setPlaceholderImage(placeholderImage: HTMLImageElement): void {
    this.placeholderImage = placeholderImage;
  }

  setVideoEnabled(isVideoEnabled: boolean): void {
    this.isVideoEnabled = isVideoEnabled;
  }

  grabFrameSource(): HTMLVideoElement | HTMLImageElement | undefined {
    const shouldGrabFrameFromVideo = this.isVideoEnabled && this.isVideoPlaying && this.videoElement;
    return shouldGrabFrameFromVideo ? this.videoElement : this.placeholderImage;
  }

  grabWhenAvailable(): HTMLCanvasElement | null {
    const frameSource = this.grabFrameSource();
    if (frameSource) {
      this.canvasContext.drawImage(
        frameSource,
        0,
        0,
        this.canvasContext.canvas.width,
        this.canvasContext.canvas.height
      );
      return this.canvasContext.canvas;
    }
    return null;
  }

  grabFrame(): HTMLCanvasElement {
    return this.grabWhenAvailable() ?? this.canvasContext.canvas;
  }

  setVideoPlaying(): void {
    this.isVideoPlaying = true;
  }

  setVideoPaused(): void {
    this.isVideoPlaying = false;
  }

  setupVideoListening(): void {
    if (this.videoElement) {
      this.videoElement.addEventListener('loadeddata', this.setVideoPlaying, false);
      this.videoElement.addEventListener('playing', this.setVideoPlaying, false);
      this.videoElement.addEventListener('play', this.setVideoPlaying, false);

      this.videoElement.addEventListener('ended', this.setVideoPaused, false);
      this.videoElement.addEventListener('stalled', this.setVideoPaused, false);
      this.videoElement.addEventListener('waiting', this.setVideoPaused, false);
    }
  }

  cleanupVideoListening(): void {
    if (this.videoElement) {
      this.videoElement.removeEventListener('loadeddata', this.setVideoPlaying);
      this.videoElement.removeEventListener('playing', this.setVideoPlaying);
      this.videoElement.removeEventListener('play', this.setVideoPlaying);

      this.videoElement.removeEventListener('ended', this.setVideoPaused);
      this.videoElement.removeEventListener('stalled', this.setVideoPaused);
      this.videoElement.removeEventListener('waiting', this.setVideoPaused);
    }
  }

  resizeToSource(): void {
    if (this.videoElement) {
      this.resize(this.videoElement.videoWidth, this.videoElement.videoHeight);
    }
  }

  resize(width: number, height: number): void {
    if (this.size.width === width && this.size.height === height) {
      return;
    }
    this.canvasContext.canvas.width = width;
    this.canvasContext.canvas.height = height;
  }

  destroy(): void {
    this.cleanupVideoListening();
  }
}
